import React, { useMemo } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import {
  Alert as MUIAlert,
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import {
  Check,
  Close as CloseIcon,
  NotificationsActive,
} from '@mui/icons-material'
import AppLink from '../util/AppLink'
import Timeline from './Timeline'
import { GenericError, ObjectNotFound } from '../error-pages'
import { ackLabel, ago, useAcks } from './api'
import { SeverityChip, StatusChip } from './AlertsPage'
import { FileBrowser, PayloadView, linksIn } from './Payload'
import { Page, Panel, useBrand, useNow } from './ui'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }

const query = gql`
  query ErecordsAlertDetail($id: Int!) {
    alert(id: $id) {
      id
      alertID
      status
      summary
      details
      createdAt
      service {
        id
        name
        escalationPolicy {
          id
          name
          steps {
            id
          }
        }
      }
      state {
        lastEscalation
        stepNumber
        repeatCount
      }
      pendingNotifications {
        destination
      }
    }
  }
`

const updateMutation = gql`
  mutation ErecordsAlertUpdate($input: UpdateAlertsInput!) {
    updateAlerts(input: $input) {
      id
    }
  }
`
const escalateMutation = gql`
  mutation ErecordsAlertEscalate($input: [Int!]) {
    escalateAlerts(input: $input) {
      id
    }
  }
`

function Fact(props: {
  label: string
  children: React.ReactNode
}): React.JSX.Element {
  const c = useBrand()
  return (
    <Box sx={{ minWidth: 150 }}>
      <Typography sx={{ fontSize: 12, color: c.muted }}>
        {props.label}
      </Typography>
      <Box sx={{ fontSize: 14, mt: 0.25 }}>{props.children}</Box>
    </Box>
  )
}

// AlertDetail replaces upstream's alert page: facts at the top, actions, a
// readable payload (pretty JSON, MinIO folders as file browsers), timeline.
export default function AlertDetail(props: {
  alertID: string
}): React.JSX.Element {
  const c = useBrand()
  const now = useNow()
  const id = parseInt(props.alertID, 10)
  const [q, refetch] = useQuery({
    query,
    variables: { id },
    context: noSuspense,
  })
  const [upd, commitUpdate] = useMutation(updateMutation)
  const [esc, commitEscalate] = useMutation(escalateMutation)
  const a = q.data?.alert
  const links = useMemo(() => (a ? linksIn(a.details) : []), [a?.details])
  const acks = useAcks(a && a.status !== 'StatusUnacknowledged' ? [id] : [])

  if (q.error) return <GenericError error={q.error.message} />
  if (!a) {
    return q.fetching ? (
      <Typography sx={{ p: 3, color: c.muted }}>Loading…</Typography>
    ) : (
      <ObjectNotFound />
    )
  }

  const busy = upd.fetching || esc.fetching
  const reload = (): void => refetch({ requestPolicy: 'network-only' })
  const setStatus = (newStatus: string): void => {
    commitUpdate(
      { input: { alertIDs: [id], newStatus } },
      { additionalTypenames: ['Alert'] },
    ).then(reload)
  }
  const steps = a.service?.escalationPolicy?.steps?.length ?? 0

  return (
    <Page
      plainTitle
      title={
        <React.Fragment>
          <span style={{ color: c.muted, fontWeight: 400 }}>#{a.alertID}</span>{' '}
          {a.summary}
        </React.Fragment>
      }
      maxWidth={1100}
      subtitle={
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          component='span'
          sx={{ flexWrap: 'wrap' }}
        >
          <StatusChip status={a.status} />
          <SeverityChip name={a.service?.name} />
          <span>
            {a.service && (
              <AppLink to={`/services/${a.service.id}`} sx={{ color: c.ink }}>
                {a.service.name}
              </AppLink>
            )}{' '}
            · opened {ago(a.createdAt, now)} ago (
            {new Date(a.createdAt).toLocaleString()})
          </span>
          {acks[id] && (
            <span title={new Date(acks[id].at).toLocaleString()}>
              · acknowledged by{' '}
              {acks[id].userID ? (
                <AppLink to={`/users/${acks[id].userID}`} sx={{ color: c.ink }}>
                  {acks[id].by}
                </AppLink>
              ) : (
                <b>{acks[id].by || 'someone'}</b>
              )}
              {ackLabel(acks[id], now).replace(/^[^·]*/, ' ')}
            </span>
          )}
        </Stack>
      }
      actions={
        a.status !== 'StatusClosed' ? (
          <React.Fragment>
            {a.status === 'StatusUnacknowledged' && (
              <Button
                variant='contained'
                startIcon={<Check />}
                disabled={busy}
                onClick={() => setStatus('StatusAcknowledged')}
              >
                Acknowledge
              </Button>
            )}
            <Button
              variant='outlined'
              startIcon={<CloseIcon />}
              disabled={busy}
              onClick={() => setStatus('StatusClosed')}
            >
              Close
            </Button>
            <Button
              startIcon={<NotificationsActive />}
              disabled={busy}
              onClick={() =>
                commitEscalate(
                  { input: [id] },
                  { additionalTypenames: ['Alert'] },
                ).then(reload)
              }
            >
              Escalate
            </Button>
          </React.Fragment>
        ) : undefined
      }
    >
      {(upd.error || esc.error) && (
        <MUIAlert severity='error' sx={{ mb: 2 }}>
          {(upd.error || esc.error)?.message}
        </MUIAlert>
      )}

      <Stack spacing={2}>
        <Panel>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Fact label='Escalation policy'>
              {a.service?.escalationPolicy ? (
                <AppLink
                  to={`/escalation-policies/${a.service.escalationPolicy.id}`}
                  sx={{ color: c.ink }}
                >
                  {a.service.escalationPolicy.name}
                </AppLink>
              ) : (
                'None'
              )}
            </Fact>
            <Fact label='Escalation'>
              {a.status === 'StatusClosed'
                ? 'Finished'
                : a.state
                  ? `Step ${a.state.stepNumber + 1} of ${steps}${a.state.repeatCount ? ` · repeat ${a.state.repeatCount}` : ''}`
                  : 'Not started'}
            </Fact>
            {a.state?.lastEscalation && (
              <Fact label='Last escalated'>
                {ago(a.state.lastEscalation, now)} ago
              </Fact>
            )}
            <Fact label='Notifying next'>
              {a.pendingNotifications.length ? (
                <Stack
                  direction='row'
                  spacing={0.5}
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                >
                  {a.pendingNotifications.map(
                    (p: { destination: string }, i: number) => (
                      <Chip
                        key={i}
                        size='small'
                        variant='outlined'
                        label={p.destination}
                      />
                    ),
                  )}
                </Stack>
              ) : (
                <span style={{ color: c.muted }}>Nobody pending</span>
              )}
            </Fact>
          </Box>
        </Panel>

        <Panel
          title='Payload'
          subtitle='What the sender (Grafana, BVT, …) attached to this alert.'
        >
          <PayloadView details={a.details} />
        </Panel>

        {links.map((l) => (
          <FileBrowser key={l} link={l} />
        ))}

        <Panel
          title='Timeline'
          subtitle='Every notification, acknowledgement and change, newest first.'
        >
          <Timeline alertID={id} />
        </Panel>
      </Stack>
    </Page>
  )
}
